'use client'

import React from 'react'
import Link from 'next/link'
import BlogPostLayout from '@/app/components/BlogPostLayout'
import { BlogTLDR, BlogKeyTakeaways, BlogFAQ, BlogSources, BlogLastUpdated, BlogComparisonTable, BlogAppCTA } from '@/app/components/blog/BlogAeo'
import KeyStats from '@/app/components/blog/KeyStats'

export default function PostContent() {
  return (
    <BlogPostLayout
      title="Clear Aligners Shed Microplastics — and the BPA Comes From Somewhere Else"
      author="MicroPlastics Research Desk"
      date="August 19, 2026"
      readTime="12 min read"
      heroImage="/blog/heroes/health.webp"
      heroImageAlt="Clear orthodontic aligners and retainers release microplastic particles into saliva through mechanical friction, with most particles measuring 5 to 20 micrometres"
    >
      <BlogLastUpdated iso="2026-08-19" formatted="August 19, 2026" />

      <BlogTLDR>
        <strong>Yes — clear aligners shed measurable microplastic into saliva,
        and the effect has now been reproduced by several independent
        groups.</strong>{' '}
        When researchers soaked seven aligner brands in artificial saliva for
        seven days and stirred them five hours a day to mimic chewing, every
        brand released particles, most of them{' '}
        <strong>between 5 and 20 micrometres</strong>. But two things that
        &ldquo;Invisalign microplastics&rdquo; pages almost always get wrong
        are worth knowing before you panic.{' '}
        <strong>First, Invisalign released the fewest particles of the seven
        brands tested, not the most</strong> — five of the seven were made of
        PET, and it was one of only two made of polyurethane.{' '}
        <strong>Second, the BPA that shows up in aligner patients&rsquo; saliva
        comes from the composite attachments being glued to your teeth, not
        from the tray.</strong> In a 2025 clinical study the spike appeared
        thirty minutes after bonding and was back to baseline within a week;
        putting the aligners in did not raise it. Everything measured so far is
        in vitro or short-term, nobody has counted particles in a real mouth
        over a real 14-month treatment, and no study has linked aligner wear to
        a health outcome. The practical levers are small and they are all about{' '}
        <strong>abrasion</strong>.
      </BlogTLDR>

      <BlogKeyTakeaways
        items={[
          <><strong>All seven brands tested shed particles.</strong> Seven days in artificial saliva with five hours of daily agitation produced microplastic release from every aligner in the study — this is not a one-brand problem.</>,
          <><strong>Most particles were 5–20 µm.</strong> That size band dominated across brands, and a pooled review of 11 studies found the same thing: more than half of detected particles sat in the 5–20 µm range.</>,
          <><strong>Invisalign released the fewest, Arc Angel the most.</strong> The difference was statistically significant. Brand and polymer matter more than the category label.</>,
          <><strong>Your aligner may not be the plastic you think.</strong> Five of the seven brands tested were polyethylene terephthalate (PET); only F22 and Invisalign were polyurethane.</>,
          <><strong>The BPA spike is a bonding event, not a wear event.</strong> Saliva BPA rose 30 minutes after composite attachments were bonded and returned to baseline within a week. Aligner insertion itself did not raise it above baseline.</>,
          <><strong>3D-printed aligners swap one question for another.</strong> No BPA was detected from printed aligners, but urethane dimethacrylate (UDMA) leached in every study that looked, and printed trays showed higher cytotoxicity than thermoformed ones in lab tests.</>,
          <><strong>Friction is the mechanism, so friction is the lever.</strong> Clenching, chewing on trays, scrubbing them with abrasive toothpaste and stretching a tray past its schedule all act on the same variable.</>,
          <><strong>Nobody has measured a health outcome.</strong> Every figure below comes from a lab bench or a one-week clinical follow-up. Treat this as a reason to handle trays sensibly, not a reason to stop treatment.</>,
        ]}
      />

      <KeyStats
        heading="The numbers, and where each one comes from"
        stats={[
          { value: '7 of 7', label: 'aligner brands released microplastics', context: 'Alleo, FlexiLigner, F22, Invisalign, Lineo, Arc Angel and Ortobel, after 7 days in artificial saliva stirred 5 h/day', source: 'Quinzi et al., Science of the Total Environment, 2023', sourceUrl: 'https://pubmed.ncbi.nlm.nih.gov/36603638/' },
          { value: '5–20 µm', label: 'the dominant particle size', context: 'more than 50% of detected particles across all aligners in a pooled review of 11 studies', source: 'De Stefano et al., Materials, 2025', sourceUrl: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC12156468/' },
          { value: '22,500', label: 'chewing cycles simulated at 50 N', context: 'equivalent to 1,500 swallows a day across a 15-day aligner change interval; all three trays tested released particles', source: 'Barile et al., PLOS ONE, 2025', sourceUrl: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC11798498/' },
          { value: '30 min', label: 'after bonding, when saliva BPA peaked', context: 'in 24 patients; levels returned to baseline within a week, and aligner placement itself did not raise them', source: 'Vitores-Calero et al., Dentistry Journal, 2025', sourceUrl: 'https://pubmed.ncbi.nlm.nih.gov/41440357/' },
        ]}
      />

      <h2>The short answer</h2>
      <p>
        Clear aligners are thin thermoplastic shells that sit against your
        teeth for something like 22 hours a day, get pushed on by every
        swallow, and are taken in and out several times daily. Plastic under
        repeated mechanical friction sheds. Researchers have now measured that
        shedding directly, in more than one lab, with more than one method, and
        the finding is consistent: <strong>aligners release microplastic
        particles into the fluid around them</strong>.
      </p>
      <p>
        What the research does not show is how much of that reaches you over a
        real course of treatment, or whether it does anything once it does. The
        honest summary is that the release is real and small in absolute terms,
        the particle sizes sit in a range that mostly passes through the gut,
        and the studies were all short. That is a different sentence from
        &ldquo;aligners are dangerous&rdquo;, and it is worth resisting the
        pull toward the shorter one.
      </p>

      <h2>What the research actually measured</h2>

      <h3>The seven-day saliva study</h3>
      <p>
        The foundational experiment came out of the Università Politecnica
        delle Marche and L&rsquo;Aquila in 2023. The team took aligners from
        seven manufacturers — Alleo, FlexiLigner, F22, Invisalign, Lineo, Arc
        Angel and Ortobel — immersed two of each in artificial saliva for seven
        days, and stirred them five hours a day to approximate the mechanical
        friction of teeth. Then they filtered the saliva and put the filters
        under Raman microspectroscopy to identify the polymer, and scanning
        electron microscopy to count and size what came off.
      </p>
      <p>
        Two results matter. The first is chemical:{' '}
        <strong>five of the seven aligners were polyethylene terephthalate —
        PET, the same polymer family as a drinks bottle</strong> — while F22 and
        Invisalign were polyurethane. If you assumed all clear aligners are the
        same material, they are not, and the brand your orthodontist uses
        determines which one is in your mouth.
      </p>
      <p>
        The second is the count. The highest particle release came from Arc
        Angel and <strong>the lowest from Invisalign</strong>, and the
        difference was statistically significant. Particle sizes clustered
        between 5 and 20 µm across the board with no significant difference
        between groups, though the researchers did find a highly significant
        correlation between the size distribution and the manufacturer. In
        other words, the brands differ in how much they shed and in the shape
        of what they shed, and the market leader was at the good end of that
        range.
      </p>

      <h3>The chewing-simulation study</h3>
      <p>
        A 2025 paper in PLOS ONE approached the same question mechanically
        rather than chemically. The team loaded three appliances — Essix Ace
        (0.75 mm thermoformed PET-G), Ghost Aligner (0.75 mm thermoformed
        polyurethane) and Invisalign (0.5 mm polyurethane blend, additively
        manufactured) — through <strong>22,500 compression cycles at 50
        newtons</strong>, a number derived from roughly 1,500 swallows a day
        across a 15-day aligner interval.
      </p>
      <p>
        Every tray shed. The interesting part is that the particles were not
        the same size. Essix Ace produced the largest debris, with major axes
        of 30–111 µm; Ghost Aligner sat in the middle at 17–81 µm; and{' '}
        <strong>Invisalign produced the smallest particles, 12–23 µm</strong>.
        The authors noted that most of the Essix Ace and Ghost Aligner
        particles exceeded 20 µm, which points toward passing through the gut
        rather than being taken up by tissue.
      </p>
      <p>
        Read carefully, that is not an unambiguous win for the low-shedding
        brand. Fewer, smaller particles and more, larger particles are
        different trade-offs, and{' '}
        <Link href="/blog/nanoplastics-vs-microplastics" className="text-forest underline">the smaller a particle gets, the more of the body it can reach</Link>.
        Anyone telling you the literature crowns a clear winner has read one of
        these two studies and not the other.
      </p>
      <p>
        One more detail from that paper is genuinely actionable: the Ghost
        Aligner&rsquo;s mechanical performance degraded sharply after about
        cycle 16,750 — roughly day 11 of a 15-day interval. Trays wear out on a
        schedule, and stretching one past its change date is asking a
        structurally tired piece of plastic to keep working.
      </p>

      <h3>What the pooled review found</h3>
      <p>
        A 2025 systematic review in <em>Materials</em> gathered 11 studies —
        four straight in vitro, seven testing material that had been aged in
        real mouths first — and found the picture above holds up. Particles of
        5–20 µm made up more than half of what was detected across all
        aligners; particles above 20 µm accounted for 30–50%; and{' '}
        <strong>particles below 5 µm turned up in only three brands</strong> —
        Alleo at 17%, F22 at 18% and Invisalign at 14%.
      </p>
      <p>
        The reviewers were also blunt about the limits. Only one of the 11
        studies measured microplastic release directly. Most evidence comes
        from in vitro work that cannot reproduce real-world conditions. Many
        manufacturers use proprietary materials of undisclosed composition. And
        nobody has systematically looked at what diet, oral hygiene habits or
        bruxism do to the numbers — which, given that the mechanism is
        friction, are probably the three biggest variables there are.
      </p>

      <BlogComparisonTable
        caption="What the three main studies tested, and what each one found (checked August 2026)"
        columns={[
          { key: 's', label: 'Study' },
          { key: 'm', label: 'Method' },
          { key: 'f', label: 'Key finding' },
        ]}
        rows={[
          { s: 'Quinzi et al., 2023', m: '7 brands, 7 days in artificial saliva, stirred 5 h/day; Raman + SEM', f: 'All shed. Arc Angel highest, Invisalign lowest. Most particles 5–20 µm. Five brands were PET, two polyurethane.' },
          { s: 'Barile et al., 2025', m: '3 appliances, 22,500 compression cycles at 50 N', f: 'All shed. Particle size differed sharply by tray: Essix Ace 30–111 µm, Ghost 17–81 µm, Invisalign 12–23 µm.' },
          { s: 'De Stefano et al., 2025', m: 'Systematic review of 11 studies', f: '>50% of particles 5–20 µm; 30–50% above 20 µm; sub-5 µm found in only three brands. Only one study measured release directly.' },
          { s: 'Vitores-Calero et al., 2025', m: '24 patients, 4 saliva samples, mass spectrometry, 1-week follow-up', f: 'BPA rose 30 min after attachment bonding, not after aligner placement, and returned to baseline within a week.' },
          { s: 'Dantagnan et al., 2025', m: 'Systematic review of 9 in vitro studies on direct-printed aligners', f: 'No BPA detected. UDMA leached in every study. Printed trays showed higher cytotoxicity than thermoformed.' },
        ]}
      />

      <h2>The BPA question, answered properly</h2>
      <p>
        This is where most of the internet goes wrong, and it is the single
        most useful thing on this page.
      </p>

      <h3>What the clinical study did</h3>
      <p>
        Researchers at the University of Valencia recruited 24 patients about
        to start aligner treatment and split them into two groups of 12: one
        treated with Invisalign, one with aligners thermoformed in-office from
        0.762 mm Ortolan sheets. They took four saliva samples per patient at
        different points, froze them at &minus;80 °C, and ran them through mass
        spectrometry, with a one-week follow-up.
      </p>

      <h3>What they found</h3>
      <p>
        Saliva BPA rose significantly — but{' '}
        <strong>the rise came half an hour after the composite attachments were
        bonded to the teeth</strong>, in both groups. Attachments are the small
        tooth-coloured bumps of resin composite that give the tray something to
        push against. They are a dental filling material, and resin composites
        are a known, well-documented source of transient BPA release.
      </p>
      <p>
        Within a week, levels were back where they started. And in the
        Invisalign group, the values recorded after the aligners themselves
        went in were not significantly different from baseline. The
        differentiation from the peak and the return to baseline was actually
        faster in the in-office group.
      </p>
      <p>
        So the accurate version of &ldquo;aligners release BPA&rdquo; is:{' '}
        <strong>the glue-on attachments produce a brief BPA spike on the day
        they are placed, and the tray itself did not measurably raise
        saliva BPA at all.</strong> If BPA specifically is your concern, the
        appointment to worry about is the bonding visit, not the fourteen
        months after it. Rinsing thoroughly and drinking water for the rest of
        that day is a reasonable, proportionate response. Refusing treatment is
        not.
      </p>
      <p>
        Worth keeping straight, too:{' '}
        <Link href="/blog/is-bpa-free-plastic-safe" className="text-forest underline">&ldquo;BPA-free&rdquo; and &ldquo;does not shed particles&rdquo; are unrelated claims</Link>.
        Align states its SmartTrack material is BPA-free, and nothing in the
        particle research contradicts that. Bisphenol A is a chemical that
        migrates out of certain polymers; a microplastic is a fragment of the
        polymer itself. A tray can be entirely free of the first and still
        produce the second, which is exactly what the studies above describe.
      </p>

      <h2>Where 3D-printed aligners fit</h2>
      <p>
        Direct 3D printing is displacing thermoforming across the industry, and
        the Invisalign tray in the 2025 loading study was additively
        manufactured rather than pressed over a mould. That changes the
        chemistry, so the older literature does not automatically transfer.
      </p>
      <p>
        A 2025 systematic review in <em>International Orthodontics</em> screened
        524 papers and found nine that examined printed aligners. The results
        cut both ways. <strong>No BPA release was detected</strong> from printed
        aligners in the studies that looked for it, and no estrogenic effects
        were found. But <strong>urethane dimethacrylate leached in every single
        study</strong> that measured monomer release, and compared with
        thermoformed trays, direct-printed aligners showed{' '}
        <em>higher</em> cytotoxicity in cell assays. The reviewers flagged
        post-curing — how thoroughly the resin is hardened after printing — as
        the step that determines whether a printed tray behaves acceptably.
      </p>
      <p>
        All nine studies were in vitro with a moderate risk of bias, and the
        authors concluded there is no consensus yet. Printed aligners are not
        obviously better or worse; they are less studied, and they trade a
        bisphenol question for a methacrylate one.
      </p>

      <BlogAppCTA
        headline="Not sure what the plastic in your bathroom actually is?"
        sub="Scan a retainer case, a toothpaste tube or a mouthguard box and get the polymer, a 0–100 risk score, and a lower-plastic swap in about five seconds."
      />

      <h2>Retainers and night guards are the longer exposure</h2>
      <p>
        Aligner treatment ends. Retention does not. A course of clear aligners
        runs somewhere between six and eighteen months for most people, and is
        then followed by a retainer worn nightly — often indefinitely. A night
        guard for bruxism is the same arrangement, worn by someone whose
        defining habit is grinding hard on it.
      </p>
      <p>
        Nobody has studied this properly, which is a real gap given the
        arithmetic. Eight hours a night for ten years is more contact time than
        22 hours a day for a year, and grinding applies far more force than
        swallowing. The Essix material in the loading study is the same
        thermoplastic used for a large share of retainers, and it produced the
        largest particles of the three tested.
      </p>
      <p>
        A few practical implications follow from the mechanism rather than from
        direct evidence, and are worth stating as such:
      </p>
      <ul>
        <li>
          <strong>A bonded lingual wire retainer involves no full-coverage
          plastic at all.</strong> It is a thin wire cemented behind the front
          teeth. If you want the lowest-plastic retention option, that is the
          conversation to have with your orthodontist — it has its own
          trade-offs around hygiene and breakage, and it does not suit every
          case.
        </li>
        <li>
          <strong>A worn, cloudy, scratched retainer is a retainer that has
          been abrading.</strong> Visible surface damage is the mechanism made
          visible. Replacing one that has gone hazy is not vanity.
        </li>
        <li>
          <strong>If you grind, treat the guard as a consumable.</strong> A
          night guard chewed to a translucent lip is doing its job by being
          destroyed, and the material it loses has to go somewhere.
        </li>
      </ul>

      <h2>What to actually do</h2>
      <p>
        Every lever here acts on friction, heat or time, because those are what
        the studies varied. In rough order of how much they matter:
      </p>
      <ul>
        <li>
          <strong>Take the trays out for anything but cool water.</strong> This
          is standard orthodontic advice for staining and decay reasons, and it
          happens to be the right advice here too. Hot coffee softens the
          polymer surface; sugary drinks trapped under a tray are a separate
          problem entirely.
        </li>
        <li>
          <strong>Do not clean them with toothpaste.</strong> Toothpaste is an
          abrasive — that is its function — and scrubbing a tray with it every
          morning is a small daily sanding operation on the exact surface the
          research is measuring. Cool water, a soft brush kept for the purpose,
          and clear unscented soap or a dedicated aligner cleaner do the job
          without the grit. Whitening pastes are the worst offenders.
        </li>
        <li>
          <strong>Never rinse or soak in hot water.</strong> It warps
          thermoplastic and accelerates surface degradation. Both effects work
          against you.
        </li>
        <li>
          <strong>Change trays on schedule rather than stretching them.</strong>{' '}
          One of the three appliances in the loading study lost mechanical
          integrity around day 11 of a 15-day interval. Wearing week three of a
          two-week tray is not thrift.
        </li>
        <li>
          <strong>Stop chewing on them.</strong> Idle clenching, chewing the
          edges, using chewies more than instructed — all of it is the
          experimental condition, applied voluntarily.
        </li>
        <li>
          <strong>Rinse well after the attachment-bonding appointment.</strong>{' '}
          That is the visit where the measured BPA spike actually happens, and
          it resolves within a week.
        </li>
      </ul>

      <h3>What is not worth doing</h3>
      <p>
        Abandoning orthodontic treatment over this is not a proportionate
        response to the evidence, and malocclusion has documented consequences
        that microplastic release from a tray currently does not. Neither is
        buying a{' '}
        <Link href="/blog/do-microplastic-removal-supplements-work" className="text-forest underline">microplastic &ldquo;detox&rdquo; supplement</Link>{' '}
        — there is no evidence any of them clear particles from the body.
        Switching brands mid-treatment on the strength of one in vitro ranking
        is also hard to justify: the differences between brands were real but
        modest, and continuity of treatment is worth more.
      </p>

      <h2>What we still do not know</h2>
      <p>
        This is a young literature and it is worth being explicit about its
        edges, because the gaps are large:
      </p>
      <ul>
        <li>
          <strong>No one has counted particles in a real mouth over a real
          treatment.</strong> Every figure above comes from artificial saliva,
          a loading rig, or a one-week clinical follow-up. There is no
          particles-per-day number, and any page giving you one has made it up.
        </li>
        <li>
          <strong>No study has measured a health outcome in aligner
          wearers.</strong> The concerns raised — inflammation, oxidative
          stress, tissue accumulation — come from the broader microplastics
          literature, not from anyone following aligner patients.
        </li>
        <li>
          <strong>Swallowing is not the only route.</strong> The oral mucosa is
          thin and highly vascular, and what that means for particles at this
          size has not been established.
        </li>
        <li>
          <strong>Retainers and night guards are essentially unstudied</strong>{' '}
          despite representing the longest exposure of all.
        </li>
        <li>
          <strong>Proprietary materials block comparison.</strong> Several
          manufacturers do not disclose composition, so a review can report
          what came off a tray without being able to say what the tray was.
        </li>
      </ul>

      <p>
        Related reading:{' '}
        <Link href="/blog/do-contact-lenses-shed-microplastics" className="text-forest underline">do contact lenses shed microplastics</Link>,{' '}
        <Link href="/blog/do-gel-and-acrylic-nails-shed-microplastics" className="text-forest underline">gel and acrylic nails</Link>,{' '}
        <Link href="/blog/best-microplastic-free-toothpaste" className="text-forest underline">microplastics in toothpaste</Link>,{' '}
        <Link href="/blog/is-bpa-free-plastic-safe" className="text-forest underline">is BPA-free plastic safe</Link>, and{' '}
        <Link href="/blog/how-long-do-microplastics-stay-in-your-body" className="text-forest underline">how long microplastics stay in your body</Link>.
      </p>

      <BlogFAQ
        items={[
          {
            question: 'Do clear aligners release microplastics?',
            answer: 'Yes. In a 2023 study, aligners from all seven manufacturers tested released microplastic particles after seven days in artificial saliva with five hours of daily agitation, with most particles measuring 5–20 micrometres. A 2025 mechanical study reproduced the finding under 22,500 simulated swallowing cycles, and a 2025 systematic review of 11 studies reached the same conclusion. No study has yet measured how many particles a person actually swallows over a full course of treatment.',
          },
          {
            question: 'Does Invisalign release more microplastics than other aligners?',
            answer: 'No — it released the fewest of the seven brands tested. In the 2023 Quinzi study, the highest particle count came from Arc Angel and the lowest from Invisalign, a statistically significant difference. A separate 2025 loading study found Invisalign produced the smallest particles (12–23 micrometres major axis) while Essix Ace produced the largest (30–111 micrometres), so the two studies measure different things and do not name a single winner.',
          },
          {
            question: 'Does Invisalign contain BPA?',
            answer: 'Align states its SmartTrack aligner material is BPA-free, and the clinical evidence is consistent with that. A 2025 study of 24 patients found saliva BPA rose significantly 30 minutes after the composite attachments were bonded to the teeth — a resin-composite effect — and returned to baseline within a week. Placing the aligners themselves did not raise BPA above baseline in the Invisalign group. BPA-free does not mean particle-free, though: those are separate questions.',
          },
          {
            question: 'What plastic are clear aligners made of?',
            answer: 'It depends on the brand. When researchers ran Raman spectroscopy on seven aligner brands, five — Alleo, FlexiLigner, Lineo, Arc Angel and Ortobel — were polyethylene terephthalate (PET), and two — F22 and Invisalign — were polyurethane. Essix retainers and many thermoformed trays use PET-G. Increasingly, aligners are directly 3D printed from methacrylate resins rather than thermoformed at all.',
          },
          {
            question: 'Are 3D-printed aligners safer than thermoformed ones?',
            answer: 'Not clearly. A 2025 systematic review of nine in vitro studies found no BPA release and no estrogenic effects from direct-printed aligners, but urethane dimethacrylate (UDMA) leached in every study that measured monomer release, and printed aligners showed higher cytotoxicity than thermoformed ones. Post-curing quality was identified as decisive. The reviewers concluded there is no consensus and called for long-term research.',
          },
          {
            question: 'Do retainers and night guards shed microplastics too?',
            answer: 'Almost certainly, though they have barely been studied. Essix-type material — used for a large share of clear retainers — produced the largest particles of the three appliances in the 2025 loading study. Retainers and night guards are worn nightly for years rather than months, and grinding applies far more force than swallowing, so the exposure is plausibly larger than during active treatment. A bonded lingual wire retainer avoids full-coverage plastic entirely.',
          },
          {
            question: 'How should I clean my aligners to minimise shedding?',
            answer: 'Use cool water, a soft brush kept only for that purpose, and clear unscented soap or a dedicated aligner cleaner. Avoid toothpaste, which is an abrasive by design and sands the tray surface — the exact process the research measures — with whitening pastes the worst offenders. Never use hot water, which warps thermoplastic and speeds surface degradation. Take trays out for anything other than cool water.',
          },
          {
            question: 'Should I stop aligner treatment because of microplastics?',
            answer: 'The evidence does not support that. Particle release is real and reproducible, but every study is in vitro or short-term, none has measured a health outcome in aligner wearers, and no one has established how much reaches the body during real treatment. Untreated malocclusion has documented consequences that particle shedding from a tray currently does not. The proportionate response is to reduce abrasion — change trays on schedule, avoid abrasive cleaning, avoid heat, stop chewing on them.',
          },
        ]}
      />

      <BlogSources
        items={[
          { authors: 'Quinzi V, Orilisi G, Vitiello F, Notarstefano V, Marzo G, Orsini G', year: 2023, title: 'A spectroscopic study on orthodontic aligners: first evidence of secondary microplastic detachment after seven days of artificial saliva exposure — seven brands, five PET and two polyurethane; highest release Arc Angel, lowest Invisalign; most particles 5–20 µm', publisher: 'Science of the Total Environment 866:161356', url: 'https://pubmed.ncbi.nlm.nih.gov/36603638/' },
          { authors: 'Barile G, Cianci C, Paramsamy Kannan V, Pappalettera G, Pappalettere C, Casavola C, Laurenziello M, Ciavarella D', year: 2025, title: 'Experimental assessment of damage and microplastic release during cyclic loading of clear aligners — 22,500 cycles at 50 N; particle major axes 30–111 µm (Essix Ace), 17–81 µm (Ghost Aligner), 12–23 µm (Invisalign)', publisher: 'PLOS ONE', url: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC11798498/' },
          { authors: 'De Stefano AA, Horodynski M, Galluccio G', year: 2025, title: 'Can clear aligners release microplastics that impact the patient’s overall health? A systematic review — 11 studies; >50% of particles 5–20 µm, 30–50% above 20 µm, sub-5 µm in only three brands', publisher: 'Materials (Basel)', url: 'https://pmc.ncbi.nlm.nih.gov/articles/PMC12156468/' },
          { authors: 'Vitores-Calero A, García-Sanz V, Paredes-Gallardo V, Zamora-Martínez N, Tarazona-Álvarez B', year: 2025, title: 'Quantification of bisphenol A in the saliva of patients wearing clear aligners — 24 patients; significant BPA rise 30 minutes after attachment bonding, returning to baseline within a week', publisher: 'Dentistry Journal (Basel) 13(12):599', url: 'https://pubmed.ncbi.nlm.nih.gov/41440357/' },
          { authors: 'Dantagnan CA, Babajko S, Nassif A, Porporatti A, Attal JP, Dursun E, Nguyen JF, Bosco J', year: 2025, title: 'Biocompatibility of direct printed clear aligners: a systematic review of in vitro studies — 9 studies; no BPA detected, UDMA leaching reported in all, higher cytotoxicity than thermoformed aligners', publisher: 'International Orthodontics 23(4):101028', url: 'https://pubmed.ncbi.nlm.nih.gov/40441042/' },
        ]}
      />
    </BlogPostLayout>
  )
}
